import React, { useEffect, useMemo, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import RestService from '../Services/RestService';
import OKomBreadcrumb from '../Components/OsloKommuneUI/OKomBreadcrumb';
import OKomLoading from '../Components/OsloKommuneUI/OKomLoading';
import { useUserContext } from '../Components/StateContext/UserContext';

// Human-readable Norwegian labels for the audited fields.
const FIELD_LABELS: { [key: string]: string } = {
  Firstname: 'Fornavn',
  Lastname: 'Etternavn',
  FirstnameTamil: 'Fornavn (tamil)',
  LastnameTamil: 'Etternavn (tamil)',
  DateOfBirth: 'Fødselsdato',
  Gender: 'Kjønn',
  House: 'Lag (hus)',
};

const entityTypeLabel = (t: string): string =>
  t === 'exam' ? 'Eksamen' : t === 'athletics' ? 'Friidrett' : t;

// Display a single field value in Norwegian (dates, gender and house get friendly forms).
const formatValue = (field: string, value: any): string => {
  if (value == null || value === '') return '–';
  if (field === 'Gender') return value === 'boy' ? 'Gutt' : value === 'girl' ? 'Jente' : String(value);
  if (field === 'House') return value === 'rød' ? 'Rød' : value === 'gul' ? 'Gul' : String(value);
  if (field === 'DateOfBirth') {
    const d = new Date(value);
    if (!isNaN(d.getTime())) {
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      return `${dd}.${mm}.${d.getFullYear()}`;
    }
  }
  return String(value);
};

const formatDateTime = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('nb-NO') + ' ' + d.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' });
};

// The edited participant's name, taken from the after-snapshot (falls back to the before-snapshot).
const entryName = (e: Api.LocalAdminAuditLog): string => {
  let obj: any = {};
  try { obj = JSON.parse(e.NewValues || e.OldValues || '{}'); } catch { /* keep empty */ }
  return `${obj.Firstname || ''} ${obj.Lastname || ''}`.trim() || '–';
};

interface Change { field: string; from: any; to: any; }

// Parse the before/after JSON snapshots and return only the fields that actually changed.
const diffChanges = (oldJson?: string, newJson?: string): Change[] => {
  let o: any = {}, n: any = {};
  try { o = JSON.parse(oldJson || '{}'); } catch { /* keep empty */ }
  try { n = JSON.parse(newJson || '{}'); } catch { /* keep empty */ }
  const keys = Array.from(new Set([...Object.keys(o), ...Object.keys(n)]));
  const changes: Change[] = [];
  keys.forEach(k => {
    const ov = o[k], nv = n[k];
    if (String(ov == null ? '' : ov) !== String(nv == null ? '' : nv)) {
      changes.push({ field: k, from: ov, to: nv });
    }
  });
  return changes;
};

const AuditLogView: React.FC<RouteComponentProps> = () => {
  const { userRoles } = useUserContext();
  const isAdmin = userRoles.includes('admin');

  const [isLoading, setIsLoading] = useState(false);
  const [entries, setEntries] = useState<Api.LocalAdminAuditLog[]>([]);
  const [departments, setDepartments] = useState<Api.Department[]>([]);

  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        setIsLoading(true);
        const data = await RestService.get<Api.LocalAdminAuditLog[]>('/api/auditlog/Recent');
        setEntries(data || []);
      } catch (e) {
        toast.error(`Kunne ikke laste endringslogg: ${e}`);
      } finally {
        setIsLoading(false);
      }
    })();
    RestService.get<Api.Department[]>('/api/examregistration/GetDepartments')
      .then(d => setDepartments(d || []))
      .catch(() => { /* department names are best-effort */ });
  }, [isAdmin]);

  const deptNameMap = useMemo(() => new Map(departments.map(d => [d.Id, d.Name])), [departments]);
  const deptLabel = (id?: number) => (id != null ? deptNameMap.get(id) || '' : '');

  if (!isAdmin) {
    return (
      <>
        <OKomBreadcrumb path='Påmelding, Endringslogg' />
        <p className="ods-text--align-center ods-margin-top-5">Du har ikke tilgang til denne siden.</p>
      </>
    );
  }

  const thStyle: React.CSSProperties = { textAlign: 'left', padding: '0.4rem 0.6rem', borderBottom: '2px solid #ccc', whiteSpace: 'nowrap' };
  const tdStyle: React.CSSProperties = { padding: '0.4rem 0.6rem', borderBottom: '1px solid #eee', verticalAlign: 'top' };

  return (
    <>
      <OKomBreadcrumb path='Revisjonslogg' />
      {isLoading && <OKomLoading text='Henter endringslogg …' />}

      <div className="ods-margin-top-5" style={{ padding: '0 1.5rem', maxWidth: '70rem', margin: '0 auto' }}>
        <h1 className='ods-text--size-jumbo'>Endringslogg</h1>
        <p className="ods-color-text-grayscale-10">Endringer gjort av lokaladministratorer på påmeldinger. Viser de {entries.length} nyeste.</p>

        <div className="ods-margin-top-3" style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead>
              <tr>
                <th style={thStyle}>Tidspunkt</th>
                <th style={thStyle}>Endret av</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Avdeling</th>
                <th style={thStyle}>Navn</th>
                <th style={thStyle}>Endringer</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(e => {
                const changes = diffChanges(e.OldValues, e.NewValues);
                return (
                  <tr key={e.Id}>
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{formatDateTime(e.CreatedDate)}</td>
                    <td style={tdStyle}>{e.UserEmail || `#${e.UserId}`}</td>
                    <td style={tdStyle}>{entityTypeLabel(e.EntityType)}</td>
                    <td style={tdStyle}>{deptLabel(e.DepartmentId) || '–'}</td>
                    <td style={tdStyle}>{entryName(e)}</td>
                    <td style={tdStyle}>
                      {changes.length === 0 ? (
                        <span className="ods-color-text-grayscale-10">(ingen endring)</span>
                      ) : (
                        changes.map(c => (
                          <div key={c.field}>
                            <strong>{FIELD_LABELS[c.field] || c.field}:</strong>{' '}
                            {formatValue(c.field, c.from)} → {formatValue(c.field, c.to)}
                          </div>
                        ))
                      )}
                    </td>
                  </tr>
                );
              })}
              {entries.length === 0 && !isLoading && (
                <tr><td style={tdStyle} colSpan={6} className="ods-text--align-center ods-color-text-grayscale-10">Ingen registrerte endringer.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default withRouter(AuditLogView);
